import { create } from 'zustand'
import { localStore } from 'utils/storage'
import { AIDA_DOCK_WIDTH_PX, clampDockWidth } from '../utils/constants/presentation'
import {
  AIDA_DOCK_WIDTH_STORAGE_KEY,
  LEGACY_AIDA_DOCK_WIDTH_KEY,
} from '../utils/constants/storage-keys'
import {
  clampFloatingPanelPosition,
  defaultFloatingPanelPosition,
} from '../utils/clamp-floating-position'
import { readFloatingPanelViewport } from '../utils/read-floating-panel-viewport'
import { closeAidaMediaLightbox, isAidaCloseSuppressed } from '../components/lightbox/store'
import { migrateLegacyAidaKeys } from './migrate-legacy-keys'
import type { AidaOverlaySession } from './overlay-session'
import { readAidaOverlaySession, writeAidaOverlaySession } from './overlay-session'

export interface AidaOverlayOrigin {
  top: number
  left: number
  width: number
  height: number
}

export type AidaPresentationMode = 'fullscreen' | 'dock' | 'floating'

export const DEFAULT_AIDA_PRESENTATION_MODE: AidaPresentationMode = 'dock'

export interface AidaFloatingPosition {
  x: number
  y: number
}

interface AidaOverlayState {
  open: boolean
  origin: AidaOverlayOrigin | null
  mode: AidaPresentationMode
  floatingPosition: AidaFloatingPosition | null
  dockWidth: number
}

const closedState = (): Omit<AidaOverlayState, 'dockWidth'> => ({
  open: false,
  origin: null,
  mode: DEFAULT_AIDA_PRESENTATION_MODE,
  floatingPosition: null,
})

function currentViewportWidth() {
  if (typeof window === 'undefined') {
    return AIDA_DOCK_WIDTH_PX * 2
  }
  return window.innerWidth
}

export function readAidaDockWidth(viewportWidth: number) {
  const stored = localStore.getString(AIDA_DOCK_WIDTH_STORAGE_KEY)
  const parsed = stored === null ? Number.NaN : Number(stored)
  if (!Number.isFinite(parsed)) {
    return clampDockWidth(AIDA_DOCK_WIDTH_PX, viewportWidth)
  }
  return clampDockWidth(parsed, viewportWidth)
}

function overlayStateFromSession(
  session: AidaOverlaySession | null,
): Omit<AidaOverlayState, 'dockWidth'> {
  if (!session?.open) {
    return closedState()
  }
  const viewport = readFloatingPanelViewport()
  return {
    open: true,
    origin: null,
    mode: session.mode,
    floatingPosition:
      session.mode === 'floating'
        ? clampFloatingPanelPosition(
            session.floatingPosition ?? defaultFloatingPanelPosition(viewport),
            viewport,
          )
        : null,
  }
}

migrateLegacyAidaKeys()

export const useAidaOverlayStore = create<AidaOverlayState>(() => ({
  ...overlayStateFromSession(readAidaOverlaySession()),
  dockWidth: readAidaDockWidth(currentViewportWidth()),
}))

useAidaOverlayStore.subscribe((state, previous) => {
  if (
    state.open === previous.open &&
    state.mode === previous.mode &&
    state.floatingPosition === previous.floatingPosition
  ) {
    return
  }
  writeAidaOverlaySession({
    open: state.open,
    mode: state.mode,
    floatingPosition: state.floatingPosition,
  })
})

export function restoreAidaOverlayFromSession() {
  migrateLegacyAidaKeys()
  useAidaOverlayStore.setState(overlayStateFromSession(readAidaOverlaySession()))
}

export function openAidaOverlay(origin: AidaOverlayOrigin) {
  useAidaOverlayStore.setState({
    open: true,
    origin,
    mode: DEFAULT_AIDA_PRESENTATION_MODE,
    floatingPosition: null,
  })
}

export function openAidaOverlayFromElement(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  openAidaOverlay({
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  })
}

export function closeAidaOverlay() {
  if (isAidaCloseSuppressed()) {
    closeAidaMediaLightbox()
    return
  }
  useAidaOverlayStore.setState(closedState())
  closeAidaMediaLightbox()
}

export function resetAidaOverlay() {
  useAidaOverlayStore.setState(closedState())
  closeAidaMediaLightbox()
}

export function setAidaPresentationMode(mode: AidaPresentationMode) {
  const state = useAidaOverlayStore.getState()
  if (!state.open) {
    return
  }
  if (mode === 'floating' && !state.floatingPosition) {
    useAidaOverlayStore.setState({
      mode,
      floatingPosition: defaultFloatingPanelPosition(readFloatingPanelViewport()),
    })
    return
  }
  useAidaOverlayStore.setState({ mode })
}

export function previewAidaDockWidth(width: number) {
  const dockWidth = clampDockWidth(width, currentViewportWidth())
  if (useAidaOverlayStore.getState().dockWidth !== dockWidth) {
    useAidaOverlayStore.setState({ dockWidth })
  }
  return dockWidth
}

export function setAidaDockWidth(width: number) {
  const dockWidth = previewAidaDockWidth(width)
  if (localStore.getString(AIDA_DOCK_WIDTH_STORAGE_KEY) !== String(dockWidth)) {
    localStore.setString(AIDA_DOCK_WIDTH_STORAGE_KEY, String(dockWidth))
  }
  localStore.remove(LEGACY_AIDA_DOCK_WIDTH_KEY)
}

export function setAidaFloatingPosition(point: AidaFloatingPosition) {
  useAidaOverlayStore.setState({
    floatingPosition: clampFloatingPanelPosition(point, readFloatingPanelViewport()),
  })
}

export function snapAidaPresentationToFullscreen() {
  const state = useAidaOverlayStore.getState()
  if (!state.open || state.mode === 'fullscreen') {
    return
  }
  useAidaOverlayStore.setState({ mode: 'fullscreen' })
}
